import { useSyncExternalStore } from "react";
import { useLocalStorage } from "react-use";

export const PREVIEW_PREFERENCES_KEY = "preview";
export const MIRROR_PREFERENCE_KEY = "live:mirrorLocalVideo";

const defaultPreviewPreferences = {
  name: "",
  isAudioMuted: false,
  isVideoMuted: false,
};

/**
 * What the participant chose last time on the preview screen: their name and
 * whether they joined muted. Kept under the same localStorage key as before
 * the redesign, so returning participants keep their name.
 */
export function usePreviewPreferences() {
  const [preferences, setPreferences] = useLocalStorage(
    PREVIEW_PREFERENCES_KEY,
    defaultPreviewPreferences
  );
  return [{ ...defaultPreviewPreferences, ...preferences }, setPreferences];
}

const mirrorListeners = new Set();
// Only used when localStorage is blocked, so the switch still works until
// the page is closed.
let unsavedMirror = null;

function readMirror() {
  try {
    const stored = window.localStorage.getItem(MIRROR_PREFERENCE_KEY);
    return stored === null ? true : JSON.parse(stored) !== false;
  } catch {
    return unsavedMirror ?? true;
  }
}

function saveMirror(mirror) {
  unsavedMirror = Boolean(mirror);
  try {
    window.localStorage.setItem(
      MIRROR_PREFERENCE_KEY,
      JSON.stringify(Boolean(mirror))
    );
  } catch {
    // Storage blocked: unsavedMirror carries the choice.
  }
  mirrorListeners.forEach(listener => listener());
}

function subscribeToMirror(listener) {
  mirrorListeners.add(listener);
  return () => mirrorListeners.delete(listener);
}

/**
 * Whether the participant sees their own video mirrored (on by default).
 * One value for the whole page: the switch in Settings updates the tiles
 * already on screen (react-use's useLocalStorage kept a copy per component).
 */
export function useMirrorPreference() {
  const mirror = useSyncExternalStore(subscribeToMirror, readMirror);
  return [mirror, saveMirror];
}
